import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiFetch, setToken, getToken, loadSharedToken } from '../lib/api';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { App as CapApp } from '@capacitor/app';
import { registerNativePush, attachPushListeners } from '../lib/pushRegistration';
import { biometricEnabled, biometricAvailable } from '../lib/biometricAuth';

const AuthContext = createContext(null);
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const IS_NATIVE = Capacitor.isNativePlatform();

export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(null);
  const [loading, setLoading] = useState(true);
  /// True when a returning native user needs to re-authenticate with
  /// Face ID / Touch ID before the admin UI is shown. Orthogonal to
  /// user being set — they're still 'signed in', just gated.
  const [biometricLocked, setBiometricLocked] = useState(false);

  useEffect(() => {
    attachPushListeners();
    (async () => {
      // On native, if localStorage is empty but the viewer has already signed
      // in on this device, pull that JWT from the shared store first.
      let token = getToken();
      if (!token && IS_NATIVE) {
        token = await loadSharedToken();
      }
      if (!token) { setLoading(false); return; }
      try {
        const d = await apiFetch('/auth/me');
        if (d.user) {
          setUser(d.user);
          window.dispatchEvent(new Event('daysheets:login'));
          registerNativePush();
          // Gate the app on Face ID if the user opted in — and silently
          // disable the pref if biometrics stop being available (e.g. user
          // disabled Face ID at the OS level, or restored to a different device).
          if (IS_NATIVE && biometricEnabled()) {
            const avail = await biometricAvailable();
            if (avail) setBiometricLocked(true);
            else localStorage.setItem('ds_biometric_enabled', '0');
          }
        } else {
          setToken(null);
        }
      } catch {
        setToken(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Re-lock when the app returns from background (native only).
  useEffect(() => {
    if (!IS_NATIVE) return;
    let listener;
    CapApp.addListener('appStateChange', (state) => {
      if (!state.isActive && biometricEnabled() && user) {
        setBiometricLocked(true);
      }
    }).then(h => { listener = h; });
    return () => { if (listener) listener.remove(); };
  }, [user]);

  const unlockBiometric = () => setBiometricLocked(false);

  const login = async (email, password) => {
    const data = await apiFetch('/auth/login', { method:'POST', body: JSON.stringify({ email, password }) });
    setToken(data.token);
    setUser(data.user);
    localStorage.setItem('ds_user', JSON.stringify(data.user));
    window.dispatchEvent(new Event('daysheets:login'));
    registerNativePush();
    return data.user;
  };

  const register = async (firstName, lastName, email, password) => {
    const data = await apiFetch('/auth/register', { method:'POST', body: JSON.stringify({ firstName, lastName, email, password }) });
    setToken(data.token);
    setUser(data.user);
    localStorage.setItem('ds_user', JSON.stringify(data.user));
    window.dispatchEvent(new Event('daysheets:login'));
    registerNativePush();
    return data.user;
  };

  const loginWithGoogle = async () => {
    if (IS_NATIVE) {
      // Google blocks OAuth in embedded WebViews. Open SFSafariViewController
      // via @capacitor/browser; the backend will redirect to daysheets://
      // on success, caught by the appUrlOpen listener below.
      await Browser.open({ url: API_URL + '/auth/google/login?platform=native' });
    } else {
      window.location.href = API_URL + '/auth/google/login';
    }
  };

  const handleGoogleCallback = async (token) => {
    setToken(token);
    const d = await apiFetch('/auth/me');
    if (d.user) {
      setUser(d.user);
      localStorage.setItem('ds_user', JSON.stringify(d.user));
      window.dispatchEvent(new Event('daysheets:login'));
      registerNativePush();
      return d.user;
    }
  };

  // Native deep-link handler — catches the daysheets://auth/callback?token=JWT
  // bounce from the backend after Google OAuth completes in SFSafariViewController.
  useEffect(() => {
    if (!IS_NATIVE) return;
    let handle;
    (async () => {
      handle = await CapApp.addListener('appUrlOpen', async ({ url }) => {
        if (!url || !url.startsWith('daysheets://auth/callback')) return;
        try { await Browser.close(); } catch {}
        let token = null;
        try { token = new URL(url).searchParams.get('token'); } catch {}
        if (token) {
          await handleGoogleCallback(token);
        }
      });
    })();
    return () => { if (handle) handle.remove(); };
  }, []);

  const logout = () => {
    setToken(null);
    setUser(null);
    setBiometricLocked(false);
    localStorage.removeItem('ds_user');
  };

  return (
    <AuthContext.Provider value={{ user, login, register, loginWithGoogle, handleGoogleCallback, logout, loading, biometricLocked, unlockBiometric }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() { return useContext(AuthContext); }
